import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { CapitalEventBreakdownTable } from "features/capital-activity/event/CapitalEventBreakdownTable";
import { useCapitalEventButtonClick } from "features/capital-activity/event/CapitalEventButtonClick";
import { CapitalEventEditDateModal } from "features/capital-activity/event/CapitalEventEditDateModal";
import { CapitalEventFinalizeModal } from "features/capital-activity/event/CapitalEventFinalizeModal";
import { CapitalEventPageMenu } from "features/capital-activity/event/CapitalEventPageMenu";
import { CapitalEventStatsCard } from "features/capital-activity/event/CapitalEventStatsCard";
import { CapitalEventTable } from "features/capital-activity/event/CapitalEventTable";
import { useWaterfallFundFamilySubEventIds } from "features/capital-activity/event/useWaterfallFundFamilySubEventIds";
import { DistributionWithholdingUploadModal } from "features/distributions/DistributionWithholdingUploadModal";
import {
  getCapitalActivityEventRetrieveQueryKey,
  getCapitalActivityInvestorOverviewRetrieveQueryKey,
  getCapitalActivitySummaryRetrieveQueryKey,
  useCapitalActivityEventRetrieveSuspense,
} from "gen/endpoints/capital-activity/capital-activity";
import {
  getFundFamilyDistributionRetrieveQueryKey,
  useDistributionWithholdingByUploadUpsert,
} from "gen/endpoints/distributions/distributions";
import { useDocumentRetrieve } from "gen/endpoints/documents/documents";
import { useEqualizationCreate } from "gen/endpoints/equalization/equalization";
import {
  getFundFamilyEventRetrieveQueryKey,
  getFundFamilySubEventFirstRetrieveQueryKey,
  getFundFamilySubEventListQueryKey,
  getNoticeDocumentGenerationStatusRetrieveQueryKey,
  getWaterfallRunListQueryKey,
  useFundFamilyEventPendingToCompleteUpdate,
  useFundFamilyEventRetrieveSuspense,
  useFundFamilySubEventFirstRetrieve,
  useNewWaterfallForEventUpsertAndRun,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useNoticeConfigRetrieve } from "gen/endpoints/notice-config/notice-config";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { DocumentStateMachineFieldsEnum } from "gen/model/documentStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { RenderCellValueProps } from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";
import { isTruthy } from "shared/utils/utils";
import { useBoolean } from "usehooks-ts";

export const CapitalEventEditPage: React.FC = () => {
  const { t } = useTranslation([
    "common",
    "capitalActivity",
    "capitalCall",
    "distribution",
  ]);
  const navigate = useNavigate();
  const { showErrorToast, showToast } = useToast();
  const queryClient = useQueryClient();

  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();

  const { data: capitalActivityData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data } = useCapitalActivityEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data: noticeConfigData } = useNoticeConfigRetrieve(fundFamilyId, {
    query: {
      retry: false,
      refetchOnWindowFocus: false,
      // this endpoint returns a 404 if notices are not enabled
      throwOnError: false,
    },
  });

  const { data: waterfallSubeventData } = useFundFamilySubEventFirstRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      type: "waterfall",
      raise_if_none: false,
    },
  );

  const { data: anySubeventExists } = useFundFamilySubEventFirstRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      raise_if_none: false,
    },
  );

  const { data: withholdingDocumentData } = useDocumentRetrieve(
    capitalActivityData.distribution_withholding_upload_document_id ?? "",
    {
      query: {
        enabled:
          !!capitalActivityData.distribution_withholding_upload_document_id,
      },
    },
  );

  const { mutateAsync: upsertDistributionWithholding } =
    useDistributionWithholdingByUploadUpsert();

  const deleteDistributionWithholding = useCallback(async () => {
    await upsertDistributionWithholding({
      fundFamilyId,
      fundFamilyEventId,
      data: {
        distribution_withholding_upload_document_id: undefined,
      },
    });
  }, [fundFamilyId, fundFamilyEventId, upsertDistributionWithholding]);

  const invalidateQueries = useCallback(
    () =>
      invalidateQueryKeys(
        queryClient,
        getFundFamilyEventRetrieveQueryKey(fundFamilyId, fundFamilyEventId),
        getCapitalActivityEventRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getFundFamilySubEventListQueryKey(fundFamilyId, fundFamilyEventId),
        getWaterfallRunListQueryKey(fundFamilyId, fundFamilyEventId),
        getCapitalActivityInvestorOverviewRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getNoticeDocumentGenerationStatusRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getFundFamilyDistributionRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getCapitalActivitySummaryRetrieveQueryKey(fundFamilyId),
        getFundFamilySubEventFirstRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
          {
            type: "waterfall",
            raise_if_none: false,
          },
        ),
        getFundFamilySubEventFirstRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
          {
            raise_if_none: false,
          },
        ),
      ),
    [fundFamilyId, fundFamilyEventId, queryClient],
  );

  const { mutateAsync: completeEvent, isPending } =
    useFundFamilyEventPendingToCompleteUpdate({
      mutation: {
        onSuccess: async () => await invalidateQueries(),
      },
    });

  const onFinish = async () => {
    try {
      await completeEvent({
        fundFamilyId,
        fundFamilyEventId,
      });
    } catch (err) {
      showErrorToast(err);
      return;
    }
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({}).$,
    );
  };

  const {
    isOpen: isEditDateModalOpen,
    onOpen: openEditDateModal,
    onClose: closeEditDateModal,
  } = useDisclosure();

  const {
    isOpen: isNoticeFinalModalOpen,
    onOpen: openNoticeFinalModal,
    onClose: closeNoticeFinalModal,
  } = useDisclosure();

  const runningFundFamilySubEventIds = useWaterfallFundFamilySubEventIds();

  const { mutateAsync: createCapitalActivityEqualization } =
    useEqualizationCreate({
      mutation: {
        onError: showErrorToast,
      },
    });

  const confirmDeleteWithholding = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog,
    {
      title: t("distribution:withholding.removeModal.title"),
      description: t("distribution:withholding.removeModal.warningMessage"),
      successMessage: t("distribution:withholding.removeModal.successMessage"),
      performDelete: async () => {
        await deleteDistributionWithholding();
        await invalidateQueries();
      },
    },
  );

  const isWitholdingFailedOrOutOfDate =
    withholdingDocumentData &&
    (withholdingDocumentData.state ===
      DocumentStateMachineFieldsEnum.failed_ingestion ||
      withholdingDocumentData.state ===
        DocumentStateMachineFieldsEnum.out_of_date);

  const {
    value: isWithholdingModalOpen,
    setFalse: closeWithholdingModal,
    setTrue: openWithholdingModal,
  } = useBoolean(false);

  const isCeleryTaskProcessing = (
    state: CeleryWorkflowStateMachineFieldsEnum | undefined | null,
  ): boolean => {
    const processing_states: Array<string> = [
      CeleryWorkflowStateMachineFieldsEnum.processing,
      CeleryWorkflowStateMachineFieldsEnum.draft,
    ];

    return !state ? false : processing_states.includes(state);
  };

  const isCellLoading = useCallback(
    ({ cell }: RenderCellValueProps) =>
      Boolean(
        (cell.metadata?.type === "capital_activity" &&
          isCeleryTaskProcessing(cell.metadata.celery_state)) ||
          (cell.metadata?.type === "capital_activity" &&
            cell.metadata.cell_type == "waterfall" &&
            isTruthy(cell.metadata.fund_family_sub_event_id) &&
            runningFundFamilySubEventIds.includes(
              cell.metadata.fund_family_sub_event_id,
            )),
      ),
    [runningFundFamilySubEventIds],
  );

  const isNoticesActive = !!noticeConfigData?.is_enabled;

  const onButtonClick = useCapitalEventButtonClick(
    fundFamilyId,
    fundFamilyEventId,
    () =>
      createCapitalActivityEqualization({
        fundFamilyId,
        fundFamilyEventId,
        data: {},
      }),
    invalidateQueries,
  );

  const { mutateAsync: createAndRunWaterfall } =
    useNewWaterfallForEventUpsertAndRun({
      mutation: {
        onSuccess: async () => {
          await invalidateQueries();
        },
      },
    });

  const waterfallFundFamilySubEventId =
    waterfallSubeventData?.fund_family_subevent_data?.id;

  const isWaterfallSubeventActive = useFeatureFlagActive("waterfall_subevent");
  // can only add withholding if all waterfalls have been run and are up to date
  const canAddWithholding =
    (isWaterfallSubeventActive && waterfallFundFamilySubEventId) ||
    (data.waterfalls_up_to_date.length > 0 &&
      data.waterfalls_not_run.length === 0 &&
      data.waterfalls_out_of_date.length === 0);

  const canRunWaterfall = !!anySubeventExists?.fund_family_subevent_data;

  return (
    <PageContent>
      <PageHeader
        title={capitalActivityData.event_type_title}
        backButton={{
          title: t("common:capitalActivity"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({}).$,
        }}
        badge={getCapitalActivityEventStateBadge(capitalActivityData.state, t)}
        actions={
          <>
            {isWitholdingFailedOrOutOfDate && (
              <Badge colorScheme="warning">
                {t("distribution:withholding.outOfDateWarning")}
              </Badge>
            )}
            {isWaterfallSubeventActive &&
              (waterfallFundFamilySubEventId ? (
                <Button
                  colorScheme="secondary"
                  onClick={() =>
                    navigate(
                      rootRoute({})
                        .fundFamilies({})
                        .fundFamily({ fundFamilyId })
                        .capitalActivity({})
                        .capitalActivityEvent({ fundFamilyEventId })
                        .waterfall({})
                        .review({
                          fundFamilySubEventId: waterfallFundFamilySubEventId,
                        }).$,
                    )
                  }
                >
                  {t("capitalActivity:waterfalls.view")}
                </Button>
              ) : (
                <Restricted permission={PermissionTypeEnum.write}>
                  <Button
                    colorScheme="tertiary"
                    onClick={() =>
                      createAndRunWaterfall({
                        fundFamilyId,
                        fundFamilyEventId,
                      })
                    }
                    isDisabled={!canRunWaterfall}
                  >
                    {t("capitalActivity:waterfalls.run")}
                  </Button>
                </Restricted>
              ))}
            {!isWaterfallSubeventActive && (
              <Tooltip
                label={t("distribution:withholding.button.tooltip")}
                isDisabled={!!canAddWithholding || !!withholdingDocumentData}
              >
                <Button
                  colorScheme={
                    withholdingDocumentData ? "tertiary" : "secondary"
                  }
                  onClick={() =>
                    withholdingDocumentData
                      ? confirmDeleteWithholding()
                      : openWithholdingModal()
                  }
                  isDisabled={!canAddWithholding && !withholdingDocumentData}
                >
                  {withholdingDocumentData
                    ? t("distribution:withholding.button.remove")
                    : t("distribution:withholding.button.add")}
                </Button>
              </Tooltip>
            )}
            <Restricted permission={PermissionTypeEnum.approve}>
              <Button
                onClick={isNoticesActive ? openNoticeFinalModal : onFinish}
                isLoading={isPending}
                isDisabled={
                  isWaterfallSubeventActive && !waterfallFundFamilySubEventId
                }
              >
                {t("capitalActivity:finalizeEvent")}
              </Button>
            </Restricted>
            <CapitalEventPageMenu
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={fundFamilyEventId}
              activityData={capitalActivityData}
            />
          </>
        }
      />
      <PageBody>
        <Box mb="16px">
          <CapitalEventStatsCard
            capitalActivityData={capitalActivityData}
            openEditDateModal={openEditDateModal}
            isDraftEvent={true}
            waterfallFundFamilySubEventId={waterfallFundFamilySubEventId!}
            confirmDeleteWithholding={confirmDeleteWithholding}
            openWithholdingModal={openWithholdingModal}
            showWithholdings={
              waterfallSubeventData?.fund_family_subevent_data?.metadata
                ?.waterfall_methodology == "run_new_waterfall"
            }
          />
        </Box>
        {!!data.waterfalls_out_of_date.length && (
          <Alert
            status="error"
            mb="24px"
            data-ls-enabled={true}
            data-public={true}
          >
            <AlertIcon />
            <AlertDescription>
              {t("capitalActivity:editScreen.waterfallOutOfDate", {
                fund_names: data.waterfalls_out_of_date,
                count: data.waterfalls_out_of_date.length,
              })}
            </AlertDescription>
          </Alert>
        )}
        <CapitalEventTable
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
          isCellLoading={isCellLoading}
          onButtonClick={onButtonClick}
        />
        <Box marginBottom={8}>
          <CapitalEventBreakdownTable
            fundFamilyId={fundFamilyId}
            fundFamilyEventId={fundFamilyEventId}
            isDraftEvent={true}
          />
        </Box>
      </PageBody>
      {isEditDateModalOpen && (
        <CapitalEventEditDateModal
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
          onCloseComplete={async () => {
            closeEditDateModal();
            await invalidateQueries();
          }}
        />
      )}
      {isNoticesActive && isNoticeFinalModalOpen && (
        <CapitalEventFinalizeModal
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
          onCloseComplete={async () => {
            closeNoticeFinalModal();
            await invalidateQueries();
          }}
          onFinish={onFinish}
        />
      )}
      {isWithholdingModalOpen && (
        <DistributionWithholdingUploadModal
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
          onCloseComplete={async () => {
            await invalidateQueries();
            closeWithholdingModal();
          }}
          onSuccess={() =>
            showToast({
              variant: "success",
              message: t("distribution:withholding.addModal.successMessage"),
            })
          }
        />
      )}
    </PageContent>
  );
};
